import {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  memo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { User } from '../../../../services';
import { useTags } from '../../../hooks';
import { getCssVar } from '../../../utils';
import { IconLabel } from '../../IconLabel';
import { TextAreaField } from '../../TextAreaField';
import { UserMentionPreview } from '../../UserMentionPreview';
import { CreateCardSettingsTabType, ThreadPostFields } from '../CreateCardModal';

import classNames from 'classnames';
import classes from './ThreadPost.module.scss';

interface ThreadPostProps {
  threadPostFields: ThreadPostFields;
  setThreadPostFields: Dispatch<SetStateAction<ThreadPostFields>>;
  setActiveThreadPostSettingsTabIndex: (index: number) => void;
  setSettingsOpen: (isOpen: boolean) => void;
  canOpenSettings: boolean;
  mentionedUsers: User[];
  setUserMentionModalOpen: Dispatch<SetStateAction<boolean>>;
  postInStoryAllowMentions: boolean;
}

export const ThreadPost: FunctionComponent<ThreadPostProps> = memo(
  ({
    threadPostFields,
    setThreadPostFields,
    setActiveThreadPostSettingsTabIndex,
    setSettingsOpen,
    canOpenSettings,
    mentionedUsers,
    setUserMentionModalOpen,
    postInStoryAllowMentions,
  }) => {
    const { t } = useTranslation();

    const { hasContentTags } = useTags();

    const inputRef = useRef<HTMLTextAreaElement>(null);

    const hasTagsShortcut = canOpenSettings && hasContentTags;

    const tagsClickHandler = useCallback(() => {
      setActiveThreadPostSettingsTabIndex(CreateCardSettingsTabType.TAGS);
      setSettingsOpen(true);
    }, [setActiveThreadPostSettingsTabIndex, setSettingsOpen]);

    useEffect(() => {
      const input = inputRef.current;

      if (!input) {
        return;
      }

      input.setSelectionRange(input.value.length, input.value.length);

      setTimeout(() => input.focus(), 0);
    }, []);

    return (
      <div
        className={classNames(classes['thread-post'], {
          [classes['thread-post--mobile']]: isMobileOnly,
        })}
      >
        <div
          className={classNames(classes['thread-post__scrollable'], {
            [classes['thread-post__scrollable--mobile']]: isMobileOnly,
            [classes['thread-post__scrollable--has-tags']]: hasTagsShortcut,
          })}
        >
          <div className={classes['thread-post__description']}>
            <IconLabel
              iconId={'alert'}
              color={getCssVar('--create-card-thread-icon-color')}
              singleColor
              nonClickable
            />
            <div className={classes['thread-post__description-text']}>
              <div className={classes['thread-post__description-text--title']}>
                {t('addContent.thread-post-title')}
              </div>
              <div>{t('addContent.thread-post-description')}</div>
            </div>
          </div>

          <UserMentionPreview
            mentionedUsers={mentionedUsers}
            {...(postInStoryAllowMentions && { onClick: () => setUserMentionModalOpen(true) })}
          />

          <TextAreaField
            ref={inputRef}
            inputClassName={classes['thread-post__input']}
            value={threadPostFields.title}
            placeholder={t('addContent.thread-post-placeholder')}
            maxLength={360}
            onChange={({ target }) => setThreadPostFields({ title: target.value })}
            disableLineBreak
          />
        </div>
        <div className={classes['thread-post__pane']}>
          <div className={classes['thread-post__pane-fields']}>
            {postInStoryAllowMentions && (
              <IconLabel
                iconId={'mention'}
                iconSize={20}
                label={t('addContent.mention')}
                color={getCssVar('--create-card-link-color')}
                hoverColor={getCssVar('--create-card-link-hover-color')}
                onClick={() => setUserMentionModalOpen(true)}
              />
            )}
            {hasTagsShortcut && (
              <IconLabel
                iconId={'tags'}
                iconSize={20}
                label={t('addContent.tags')}
                color={getCssVar('--create-card-link-color')}
                hoverColor={getCssVar('--create-card-link-hover-color')}
                onClick={tagsClickHandler}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
);
